/**
 * Seasonality Utilities
 *
 * Pure helpers for building and analyzing monthly seasonality curves from
 * DRE-like row data. Extracted so that both the year-end trend projection
 * (`trend-analysis.service.ts`) and the folha gap estimator
 * (`folha-gap.service.ts`) can share the exact same math without drift.
 */

/**
 * Groups a flat list of rows into a `year → [12 monthly totals]` map.
 * Rows whose year/month can't be parsed or fall outside 1–12 are skipped.
 */
export function buildMonthlyAggregates<T>(
  rows: T[],
  getYear: (row: T) => number | null | undefined,
  getMonth: (row: T) => number | null | undefined,
  getValue: (row: T) => number,
): Map<number, number[]> {
  const byYear = new Map<number, number[]>()
  for (const row of rows) {
    const year = Number(getYear(row))
    const month = Number(getMonth(row))
    if (!year || !month || month < 1 || month > 12) continue
    if (!byYear.has(year)) byYear.set(year, new Array(12).fill(0))
    const arr = byYear.get(year)!
    arr[month - 1] += getValue(row)
  }
  return byYear
}

/**
 * Converts monthly totals per year into normalized curves where each month
 * is expressed as its share of that year's annual total (values sum to ~1).
 * Years with zero or negative annual total produce an all-zero curve.
 */
export function normalizeToPercentCurves(
  byYear: Map<number, number[]>,
): Record<number, number[]> {
  const curves: Record<number, number[]> = {}
  for (const [year, months] of byYear) {
    const yearTotal = months.reduce((s, v) => s + v, 0)
    curves[year] =
      yearTotal > 0 ? months.map((v) => v / yearTotal) : new Array(12).fill(0)
  }
  return curves
}

/**
 * Simple mean of per-year normalized curves across the provided years.
 * Returns a 12-entry array that should sum to ~1 when inputs are valid.
 */
export function averageCurveAcrossYears(
  curves: Record<number, number[]>,
  years: number[],
): number[] {
  if (years.length === 0) return new Array(12).fill(0)
  return new Array(12).fill(0).map((_, m) => {
    const sum = years.reduce((s, y) => s + (curves[y]?.[m] ?? 0), 0)
    return sum / years.length
  })
}
